// Elenca gli slot saltati in assegnazione incarichi o generazione turni perché
// il collaboratore era già impegnato (incarichi e turni si bloccano a vicenda).
//
// I conflitti `overridable` (collaboratore che ha fatto stop senza ripartire)
// si possono forzare con "Procedi comunque", con doppia conferma:
//  - `perRiga` con più slot richiesti: una checkbox per slot, si inviano solo
//    quelli spuntati;
//  - altrimenti un unico pulsante forza tutti gli overridable.
// `onConferma` riceve la lista flat dei conflitti confermati.
import React from 'react';
import { AlertTriangle, Briefcase, Shield, Clock } from 'lucide-react';

export interface ConflittoDisponibilita {
    tipo: 'INCARICO' | 'TURNO' | string;
    id: number;
    descrizione: string;
    data?: string;        // "YYYY-MM-DD"
    oraInizio?: string;   // "HH:mm"
    oraFine?: string;     // "HH:mm"
    // true se il collaboratore ha fatto stop senza ripartire: lo slot si può forzare.
    overridable?: boolean;
    // Istante dello stop (ISO-8601), solo se overridable=true.
    stopAt?: string;
    // Slot richiesto che ha generato il conflitto, per raggruppare le checkbox
    // per slot. Nulli per POST /incarichi (un solo slot per chiamata).
    richiestaData?: string;
    richiestaOraInizio?: string;
    richiestaOraFine?: string;
    richiestaNotturno?: boolean;
}

interface Props {
    open: boolean;
    onClose: () => void;
    /** Nome del collaboratore per cui c'è stato il conflitto. */
    collaboratoreNome?: string;
    /** Lista conflitti. Almeno 1 riga, altrimenti il caller non monta la modale. */
    conflitti: ConflittoDisponibilita[];
    /** Numero di slot effettivamente creati (per messaggio "X su Y assegnati"). */
    creatiCount?: number;
    /** Totale slot richiesti (per messaggio "X su Y"). */
    totaleRichiesto?: number;
    /**
     * Forza l'assegnazione dei conflitti overridable confermati. Se omesso la
     * modale è solo informativa.
     */
    onConferma?: (overrideConflitti: ConflittoDisponibilita[]) => void;
    /** True → checkbox per riga. False/omesso → conferma globale. */
    perRiga?: boolean;
}

const fmtStopAt = (s?: string): string | null => {
    if (!s) return null;
    try {
        const d = new Date(s);
        const hh = String(d.getHours()).padStart(2, '0');
        const mm = String(d.getMinutes()).padStart(2, '0');
        return `${hh}:${mm}`;
    } catch { return null; }
};

const fmtData = (d?: string) => {
    if (!d) return '';
    try { return new Date(d).toLocaleDateString('it-IT', { weekday: 'short', day: '2-digit', month: 'short', year: 'numeric' }); }
    catch { return d; }
};

// Chiave di raggruppamento per slot richiesto; senza richiesta* tutti i
// conflitti finiscono in "default".
const slotKey = (c: ConflittoDisponibilita): string =>
    [c.richiestaData || '_', c.richiestaOraInizio || '_', c.richiestaOraFine || '_', !!c.richiestaNotturno].join('|');

export const ConflittiDisponibilitaModal: React.FC<Props> = ({
    open, onClose, collaboratoreNome, conflitti, creatiCount, totaleRichiesto,
    onConferma, perRiga,
}) => {
    // Slot spuntati in modalità per-riga; in modalità singola resta vuoto.
    const [selezionati, setSelezionati] = React.useState<Set<string>>(new Set());

    // Azzera le spunte a ogni nuova lista di conflitti.
    React.useEffect(() => {
        if (open) setSelezionati(new Set());
    }, [open, conflitti]);

    if (!open) return null;

    const overridables = conflitti.filter(c => c.overridable);
    const bloccanti = conflitti.filter(c => !c.overridable);
    const tuttiOverridable = overridables.length === conflitti.length && conflitti.length > 0;
    const canConferma = !!onConferma && overridables.length > 0;

    // Overridable raggruppati per slot richiesto: le checkbox servono solo
    // con almeno due slot distinti.
    const slotKeysOverridable = Array.from(new Set(overridables.map(slotKey)));
    const inPerRiga = !!perRiga && canConferma && slotKeysOverridable.length > 1;

    const handleProcedi = () => {
        if (!onConferma) return;
        if (inPerRiga) {
            const target = overridables.filter(c => selezionati.has(slotKey(c)));
            if (target.length === 0) return;
            const nSlot = selezionati.size;
            const ok = window.confirm(
                `Confermi di assegnare ${nSlot} ${nSlot === 1 ? 'slot' : 'slot'} al collaboratore` +
                (collaboratoreNome ? ` ${collaboratoreNome}` : '') +
                ` anche se risulta impegnato ma ha cliccato stop?`
            );
            if (!ok) return;
            onConferma(target);
            return;
        }
        const ok = window.confirm(
            `Confermi di assegnare comunque al collaboratore` +
            (collaboratoreNome ? ` ${collaboratoreNome}` : '') +
            `? Il lavoro precedente resterà aperto, lui potrà completare la relazione più tardi.`
        );
        if (!ok) return;
        onConferma(overridables);
    };

    const toggleSlot = (key: string) => {
        setSelezionati(prev => {
            const next = new Set(prev);
            if (next.has(key)) next.delete(key); else next.add(key);
            return next;
        });
    };

    return (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={onClose}>
            <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg" onClick={e => e.stopPropagation()}>
                <div className="p-4 border-b border-slate-100 flex items-start gap-3">
                    <div className="p-2 bg-amber-100 text-amber-700 rounded-lg shrink-0">
                        <AlertTriangle size={18}/>
                    </div>
                    <div className="min-w-0">
                        <h3 className="text-lg font-bold text-slate-900 leading-tight">Collaboratore già impegnato</h3>
                        <p className="text-xs text-slate-500 mt-0.5">
                            {collaboratoreNome
                                ? <>Il collaboratore <strong>{collaboratoreNome}</strong> ha già altri impegni che si sovrappongono agli slot richiesti.</>
                                : 'Alcuni slot richiesti si sovrappongono ad altri impegni del collaboratore.'}
                        </p>
                        {typeof creatiCount === 'number' && typeof totaleRichiesto === 'number' && (
                            <p className="text-[11px] mt-1 font-bold text-slate-600">
                                {creatiCount} su {totaleRichiesto} slot assegnati · {conflitti.length} saltati per conflitto.
                            </p>
                        )}
                        {overridables.length > 0 && (
                            <p className="text-[11px] mt-1 text-emerald-700 bg-emerald-50 border border-emerald-200 rounded px-2 py-1">
                                {tuttiOverridable
                                    ? `Il collaboratore ha cliccato STOP sull${conflitti.length === 1 ? "'impegno in corso" : 'i impegni in corso'}: puoi assegnare comunque.`
                                    : `${overridables.length} ${overridables.length === 1 ? 'conflitto è sovrascrivibile' : 'conflitti sono sovrascrivibili'} (collaboratore ha cliccato STOP); ${bloccanti.length} ${bloccanti.length === 1 ? 'resta bloccante' : 'restano bloccanti'}.`}
                            </p>
                        )}
                    </div>
                </div>

                <div className="p-4 space-y-2 max-h-[60vh] overflow-y-auto">
                    {inPerRiga ? (
                        // Per-riga: una card con checkbox per ogni slot richiesto.
                        slotKeysOverridable.map(key => {
                            const items = overridables.filter(c => slotKey(c) === key);
                            const sample = items[0];
                            const data = sample.richiestaData;
                            const oraIni = sample.richiestaOraInizio;
                            const oraFin = sample.richiestaOraFine;
                            return (
                                <div key={key} className="flex gap-3 p-3 rounded-lg border border-emerald-200 bg-emerald-50/40">
                                    <input
                                        type="checkbox"
                                        className="mt-1 accent-emerald-600"
                                        checked={selezionati.has(key)}
                                        onChange={() => toggleSlot(key)}
                                        aria-label="Forza assegnazione di questo slot"
                                    />
                                    <div className="min-w-0 flex-1">
                                        <p className="text-[11px] font-black uppercase tracking-wider text-emerald-700">Turno richiesto</p>
                                        <p className="text-sm font-bold text-slate-800">
                                            {fmtData(data)}
                                            {oraIni && oraFin && <> · <span className="font-mono">{oraIni}–{oraFin}</span></>}
                                        </p>
                                        <div className="mt-2 space-y-1">
                                            {items.map((c, i) => {
                                                const Icon = c.tipo === 'TURNO' ? Shield : Briefcase;
                                                const stopHHmm = fmtStopAt(c.stopAt);
                                                return (
                                                    <div key={`${c.tipo}-${c.id}-${i}`} className="flex items-start gap-2 text-[11px] text-slate-600">
                                                        <Icon size={12} className="mt-0.5 shrink-0"/>
                                                        <span className="min-w-0">
                                                            <span className="font-semibold text-slate-700">{c.descrizione}</span>
                                                            {c.oraInizio && c.oraFine && (
                                                                <> · <span className="font-mono">{c.oraInizio}–{c.oraFine}</span></>
                                                            )}
                                                            {stopHHmm && (
                                                                <> · <span className="text-emerald-700 font-bold inline-flex items-center gap-0.5"><Clock size={10}/> stop {stopHHmm}</span></>
                                                            )}
                                                        </span>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </div>
                                </div>
                            );
                        })
                    ) : (
                        // Modalità singola: lista semplice, "Procedi comunque"
                        // agisce su tutti gli overridable.
                        conflitti.map((c, i) => {
                            const Icon = c.tipo === 'TURNO' ? Shield : Briefcase;
                            const color = c.tipo === 'TURNO' ? 'indigo' : 'blue';
                            const stopHHmm = fmtStopAt(c.stopAt);
                            return (
                                <div key={`${c.tipo}-${c.id}-${i}`} className={`flex gap-3 p-2.5 rounded-lg border ${c.overridable ? 'border-emerald-200 bg-emerald-50/40' : 'border-slate-200 bg-slate-50/50'}`}>
                                    <div className={`p-1.5 rounded ${color === 'indigo' ? 'bg-indigo-100 text-indigo-600' : 'bg-blue-100 text-blue-600'} shrink-0`}>
                                        <Icon size={14}/>
                                    </div>
                                    <div className="min-w-0 flex-1">
                                        <div className="flex items-center gap-2 mb-0.5">
                                            <span className={`text-[10px] font-black uppercase tracking-wider ${color === 'indigo' ? 'text-indigo-700' : 'text-blue-700'}`}>
                                                {c.tipo === 'TURNO' ? 'Turno fiduciario' : 'Incarico'}
                                            </span>
                                            {c.overridable && (
                                                <span className="text-[10px] font-bold uppercase tracking-wider text-emerald-700 bg-emerald-100 px-1.5 py-0.5 rounded inline-flex items-center gap-1">
                                                    <Clock size={10}/>
                                                    Stop {stopHHmm || ''}
                                                </span>
                                            )}
                                        </div>
                                        <p className="text-sm font-bold text-slate-800 truncate">{c.descrizione}</p>
                                        <p className="text-[11px] text-slate-500 mt-0.5">
                                            {fmtData(c.data)}
                                            {c.oraInizio && c.oraFine && <> · <span className="font-mono">{c.oraInizio}–{c.oraFine}</span></>}
                                        </p>
                                    </div>
                                </div>
                            );
                        })
                    )}
                </div>

                <div className="p-4 border-t border-slate-100 flex justify-end gap-2">
                    <button
                        onClick={onClose}
                        className="px-4 py-2 text-sm font-bold bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-lg"
                    >
                        {canConferma ? 'Annulla' : 'Ho capito'}
                    </button>
                    {canConferma && (
                        <button
                            onClick={handleProcedi}
                            disabled={inPerRiga && selezionati.size === 0}
                            className="px-4 py-2 text-sm font-bold bg-emerald-600 hover:bg-emerald-700 disabled:bg-slate-300 disabled:cursor-not-allowed text-white rounded-lg"
                        >
                            {inPerRiga
                                ? `Procedi sui ${selezionati.size} slot selezionati`
                                : `Procedi comunque (${overridables.length})`}
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
};
